import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { filterBoolean, trackByProp } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { JobsIdMap, JobsWrapperDataService } from '@core/offers-data';
import type { OfferSimpleListItem } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  TabComponent,
  TabsComponent,
} from '@core/user-interface-legacy';

type DialogData = {
  offers: OfferSimpleListItem[];
};

@Component({
  selector: 'rt-assigned-jobs-dialog',
  templateUrl: './assigned-jobs-dialog.component.html',
  styleUrls: ['./assigned-jobs-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    HeadingComponent,
    IconComponent,
    InputComponent,
    OfferStatusIndicatorComponent,
    RouterModule,
    TabComponent,
    TabsComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
})
export class AssignedJobsDialogComponent implements DialogComponentI<DialogData, void> {
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly dialog = inject<DialogRef<AssignedJobsDialogComponent>>(DialogRef);

  public searchControl = new FormControl<string>('', { nonNullable: true });

  public visibleOffers: OfferSimpleListItem[];
  public offers: OfferSimpleListItem[];
  public jobsMap: JobsIdMap;
  public trackById = trackByProp<{ id: string | number }>({ prop: 'id' });

  public setDialogData(data: DialogData, triggerUpdate: boolean) {
    this.visibleOffers = data.offers || [];
    this.offers = data.offers || [];
  }

  public hasAccessToJob(id: number): boolean {
    return !!this.jobsMap?.[id];
  }

  public ngOnInit(): void {
    this.searchControl.valueChanges.subscribe(query => {
      this.visibleOffers = this.offers.filter(offer => offer.title.includes(query));
      this.cdRef.detectChanges();
    });

    this.jobsWrapperDataService
      .getJobItemsIdMap$()
      .pipe(filterBoolean, takeUntilDestroyed(this.destroyRef))
      .subscribe(jobsMap => {
        this.jobsMap = jobsMap;
        this.cdRef.detectChanges();
      });
  }

  public close(): void {
    this.dialog.close();
  }

  public reset(): void {
    this.searchControl.reset('');
  }
}
